// ─── VUE EN ARBRE : les opérations d'un compte, par enveloppe ─────
// « Une icône en haut à droite permettant d'avoir une vue en arbre par budget
// avec des accordéons ; si pas dans un budget, la mettre sans budget »
// (Cédric, 11/08).
//
// Le relevé dit d'OÙ vient l'argent (chronologique) ; cet arbre dit à QUOI il
// sert. Même jeu d'opérations, deux questions différentes.
//
// ⚠️ Une opération répartie sur deux enveloppes apparaît dans les DEUX, avec
// la part qui les concerne — jamais en entier des deux côtés, ce qui
// doublerait l'argent. La mention « part » le dit à l'écran.
import React, { useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { Feuille, LigneSwipe, useUI, useT, frJMA, confirmerSuppression } from '@mop/core';
import { formatEuro, montantOperation, dateEffective } from './services/calculs';
import { grouperParEnveloppe } from './services/etatRattachement';
import { pourcentage } from './services/repartition';
import { emojiBudget, emojiCategorie, cleCategorie } from './data/categories';
import EnTeteArbre, { RETRAIT_ENFANTS } from './EnTeteArbre';

// Le CORPS, exporté séparément (15/08) : l'écran d'un compte le rend
// désormais EN PLACE — le 🌳 bascule la vue au lieu d'ouvrir une modale.
// La Feuille ci-dessous ne fait plus que l'habiller pour l'ancien usage.
export function ArbreEnveloppes({
  operations = [], rattachements = [], lignes = [], budgets = [],
  titre, onFiche,
  // Les feuilles se gèrent AUSSI au swipe (audit du 15/08) : → éditer
  // l'opération, ← la supprimer — les mêmes gestes que sur le relevé.
  onEditer, onSupprimer,
}) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const [ouvert, setOuvert] = useState(null);
  // Le niveau LIGNE (16/08) : ouvert par défaut — le pliage range, il ne
  // cache pas. On mémorise donc les repliées.
  const [lignesRepliees, setLignesRepliees] = useState(new Set());
  const basculerLigne = (cle) => setLignesRepliees(prev => {
    const n = new Set(prev);
    if (n.has(cle)) n.delete(cle); else n.add(cle);
    return n;
  });

  const groupes = useMemo(
    () => grouperParEnveloppe({ operations, rattachements, lignes, budgets }),
    [operations, rattachements, lignes, budgets],
  );

  return (
    <View>
      {/* Plus de ligne « n tout · total » (16/08) : le net du mois se lit
          déjà dans la carte du solde — même modèle que Budget et Suivi. */}
      {groupes.length === 0 ? (
        <Text style={s.vide}>{t('finances.aucuneOpRattachee')}</Text>
      ) : groupes.map(g => {
        const cle = g.budgetId || '__sans__';
        const plie = ouvert !== cle;
        return (
          <View key={cle}>
            {/* Grammaire commune (15/08) : rangée plate, chevron à gauche,
                total signé à droite — la carte `SectionPliable` reste aux
                pages de réglages, un arbre de données n'en est pas une. */}
            <EnTeteArbre plie={plie}
              onBasculer={() => setOuvert(plie ? cle : null)}
              emoji={g.budget ? emojiBudget(g.budget) : '∅'}
              titre={g.budget ? g.budget.nom : t('budget.sansBudget')}
              nb={g.nb}
              droite={(
                <Text style={[s.total, g.total < 0 && { color: UI.danger || '#c0392b' }]}>
                  {formatEuro(g.total)}
                </Text>
              )} />
            {/* Le niveau LIGNE (Cédric, 16/08) : entre l'enveloppe et les
                opérations, la prévision honorée — « il manque les lignes de
                budget ». Le rangement direct va dans « Sans prévision ». */}
            {!plie && <View style={{ marginLeft: RETRAIT_ENFANTS }}>
            {(g.sousLignes || []).map(sl => {
              const cleLigne = `${cle}|${sl.ligneId || '__sans__'}`;
              const ligneRepliee = lignesRepliees.has(cleLigne);
              return (
                <View key={cleLigne}>
                  <EnTeteArbre plie={ligneRepliee}
                    onBasculer={() => basculerLigne(cleLigne)}
                    emoji={sl.ligne ? emojiCategorie(sl.ligne.categorie) : '❓'}
                    titre={sl.ligne ? (sl.ligne.libelle || t(cleCategorie(sl.ligne.categorie))) : t('finances.sansPrevision')}
                    nb={sl.nb}
                    droite={(
                      <Text style={[s.totalLigne, sl.total < 0 && { color: UI.danger || '#c0392b' },
                        sl.total > 0 && { color: UI.ok || '#2e7d32' }]}>
                        {formatEuro(sl.total)}
                      </Text>
                    )} />
                  {!ligneRepliee && <View style={{ marginLeft: RETRAIT_ENFANTS }}>
                  {sl.operations.map(({ operation: o, montant, viaPrevision }, i) => {
                    // Part < opération : on le dit, sinon on chercherait en
                    // vain ce montant sur le relevé bancaire.
                    const partielle = Math.abs(Math.abs(o.montant || 0) - montant) > 0.005;
                    return (
                      <LigneSwipe key={`${o.id}-${i}`} espacement={8} enabled
                        onEditer={onEditer ? () => onEditer(o) : undefined}
                        onSupprimer={onSupprimer ? () => confirmerSuppression(
                          t('commun.supprimer'), o.libelle || formatEuro(o.montant),
                          () => onSupprimer(o)) : undefined}>
                      <TouchableOpacity style={s.op} activeOpacity={0.7}
                        onPress={() => onFiche?.(o)}>
                        <View style={{ flex: 1, minWidth: 0 }}>
                          <Text style={s.opLib} numberOfLines={1}>
                            {o.libelle || t(cleCategorie(o.categorie))}
                          </Text>
                          <Text style={s.opSous} numberOfLines={1}>
                            {frJMA(dateEffective(o))}
                            {viaPrevision ? `  ·  🔗` : ''}
                            {/* Le % de CETTE part : « 100 % » dit tout de
                                suite si l'operation est entiere ici. */}
                            {`  ·  ${pourcentage(montant, o.montant)} %`}
                            {partielle ? `  ·  ${t('finances.partDe', { total: formatEuro(Math.abs(o.montant || 0)) })}` : ''}
                          </Text>
                        </View>
                        <Text style={[s.opMontant, o.sens === 'revenu' && { color: UI.ok || '#2e7d32' }]}>
                          {montantOperation(montant, o.sens)}
                        </Text>
                      </TouchableOpacity>
                      </LigneSwipe>
                    );
                  })}
                  </View>}
                </View>
              );
            })}
            </View>}
          </View>
        );
      })}
    </View>
  );
}

export default function FeuilleArbreEnveloppes({
  visible, operations = [], rattachements = [], lignes = [], budgets = [],
  titre, onFermer, onFiche,
}) {
  const t = useT();
  return (
    <Feuille visible={visible} titre={t('finances.vueParEnveloppe')} emoji="🌳" hauteur={0.9}
      onFermer={onFermer}>
      <ArbreEnveloppes operations={operations} rattachements={rattachements}
        lignes={lignes} budgets={budgets} titre={titre} onFiche={onFiche} />
    </Feuille>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  entete: { fontSize: 12.5, color: UI.muted, fontWeight: '700', marginBottom: 12 },
  total: { fontWeight: '800', color: UI.ink },
  totalLigne: { fontSize: 13, fontWeight: '800', color: UI.muted },
  vide: { fontSize: 13, color: UI.faint, lineHeight: 18 },
  // Plus de marginBottom : l'espacement vient de LigneSwipe (espacement={8}),
  // une marge sur la carte ferait dépasser les boutons de swipe.
  op: { flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: UI.card, borderRadius: 11, borderWidth: 0.5, borderColor: UI.border, paddingVertical: 9, paddingHorizontal: 12 },
  opLib: { fontSize: 14, fontWeight: '700', color: UI.ink },
  opSous: { fontSize: 11.5, color: UI.muted, marginTop: 1 },
  opMontant: { fontSize: 14, fontWeight: '800', color: UI.ink },
});
